import { ORGANIZATIONS } from 'cpool-constants';
import { z } from 'zod';

import { Finanzbedarf } from './Finanzbedarf';
import { PrivateKrankenversicherungUndUnterhaltsverpflichtung } from './haushalt/ausgaben/PrivateKrankenversicherungUndUnterhaltsverpflichtung';
import { SonstigeAusgabeUndMietAusgabe } from './haushalt/ausgaben/SonstigeAusgabeUndMietAusgabe';
import { EhegattenunterhaltEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente } from './haushalt/einnahmen/EhegattenunterhaltEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente';
import { EinkuenfteAusNebentaetigkeit } from './haushalt/einnahmen/EinkunftAusNebentaetigkeit';
import { SonstigeEinnahme } from './haushalt/einnahmen/SonstigeEinnahme';
import { Immobilie } from './haushalt/Immobilie';
import { Kind } from './haushalt/Kind';
import { Kontoverbindung } from './haushalt/Kontoverbindung';
import { Dispositionskredit } from './haushalt/verbindlichkeiten/Dispositionskredit';
import { Immobiliendarlehen } from './haushalt/verbindlichkeiten/Immobiliendarlehen';
import { Kontokorrentkredit } from './haushalt/verbindlichkeiten/Kontokorrentkredit';
import { Kreditkarte } from './haushalt/verbindlichkeiten/Kreditkarte';
import { Leasing } from './haushalt/verbindlichkeiten/Leasing';
import { RatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit } from './haushalt/verbindlichkeiten/RatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit';
import { schemaTracking } from './Tracking';
import { schemaUTM } from './UTM';

export const schemaLoanRequest = z.object({
    coApplicantHidden: z.boolean().optional().default(false),
    correlationWebsite: z.string().nullish().default(null),
    customId: z.string().nullish().default(null),
    finanzbedarf: Finanzbedarf,
    haushalt: z.object({
        ausgaben: z
            .optional(
                z.object({
                    mietausgaben: z.array(SonstigeAusgabeUndMietAusgabe).optional().default([]),
                    privateKrankenversicherungen: z
                        .array(PrivateKrankenversicherungUndUnterhaltsverpflichtung)
                        .optional()
                        .default([]),
                    sonstigeAusgaben: z.array(SonstigeAusgabeUndMietAusgabe).optional().default([]),
                    unterhaltsverpflichtungen: z
                        .array(PrivateKrankenversicherungUndUnterhaltsverpflichtung)
                        .optional()
                        .default([]),
                }),
            )
            .describe('The expenses of the household'),
        einnahmen: z
            .optional(
                z.object({
                    ehegattenunterhalt: z
                        .array(EhegattenunterhaltEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente)
                        .optional()
                        .default([]),
                    einkuenfteAusKapitalvermoegen: z
                        .array(EhegattenunterhaltEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente)
                        .optional()
                        .default([]),
                    einkuenfteAusNebentaetigkeit: z.array(EinkuenfteAusNebentaetigkeit).optional().default([]),
                    sonstigeEinnahmen: z.array(SonstigeEinnahme).optional().default([]),
                    unbefristeteZusatzrenten: z
                        .array(EhegattenunterhaltEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente)
                        .optional()
                        .default([]),
                }),
            )
            .describe('The income of the household'),
        immobilien: z.array(Immobilie).optional().default([]).describe('The real estate of the household'),
        kinder: z.array(Kind).optional().default([]).describe('The children of the household'),
        kontoverbindung: Kontoverbindung.nullish().default(null).describe('The bank account of the household'),
        verbindlichkeiten: z
            .object({
                dispositionskredite: z.array(Dispositionskredit).optional().default([]),
                geschaeftskredite: z
                    .array(RatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit)
                    .optional()
                    .default([]),
                immobiliendarlehen: z.array(Immobiliendarlehen).optional().default([]),
                kontokorrentkredite: z.array(Kontokorrentkredit).optional().default([]),
                kreditkarten: z.array(Kreditkarte).optional().default([]),
                leasings: z.array(Leasing).optional().default([]),
                ratenkredite: z.array(RatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit).optional().default([]),
                sonstigeVerbindlichkeiten: z
                    .array(RatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit)
                    .optional()
                    .default([]),
            })
            .describe('The liabilities of the household'),
    }),
    isFeasible: z.boolean().nullish().default(false),
    isTestLead: z.boolean().optional().default(false),
    leadTS: z.number().nullish().default(null),
    organization: z.nativeEnum(ORGANIZATIONS).default(ORGANIZATIONS['kredit.de']),
    originalApplicants: z.array(z.string().min(1)).min(1).max(2),
    tracking: schemaTracking,
    utm: schemaUTM,
});

export type SchemaLoanRequest = z.infer<typeof schemaLoanRequest>;

// INFO yup schema
// static get SCHEME() {
//     const bankAccountFieldValidationOrder = [
//         ['iban', 'kreditinstitut'],
//         ['iban', 'bic'],
//     ];
//     return yup.object().shape({
//         finanzbedarf: yup.object().shape({
//             finanzierungswunsch: yup.object().shape({
//                 kreditbetrag: yup.number().optional().nullable().default(null),
//                 laufzeitInMonaten: yup.number().integer().optional().nullable().default(null),
//             }),
//             finanzierungszweck: yup
//                 .string()
//                 .oneOf(['UMSCHULDUNG', 'FAHRZEUGKAUF', 'MODERNISIEREN', 'FREIE_VERWENDUNG', null])
//                 .optional()
//                 .nullable()
//                 .default(null),
//         }),
//         haushalt: yup.object().shape({
//             immobilien: yup
//                 .array()
//                 .of(
//                     yup.object().shape({
//                         baufinanzierungsrate: yup.number().optional().nullable().default(null),
//                         bezeichnung: yup.string().optional().nullable().default(null),
//                         darlehen: yup
//                             .array()
//                             .of(
//                                 yup.object().shape({
//                                     rateMonatlich: yup.number().optional().nullable().default(null),
//                                     restschuld: yup.number().optional().nullable().default(null),
//                                     zinsbindungBis: yup.date().optional().nullable().default(null),
//                                 }),
//                             )
//                             .default([]),
//                         gehoertZuAntragsteller: yup
//                             .string()
//                             .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                             .required(),
//                         id: yup.string(),
//                         immobilienart: yup
//                             .string()
//                             .oneOf([
//                                 'EIGENTUMSWOHNUNG',
//                                 'EINFAMILIENHAUS',
//                                 'MEHRFAMILIENHAUS',
//                                 'BUEROGEBAEUDE',
//                                 null,
//                             ])
//                             .optional()
//                             .nullable()
//                             .default(null),
//                         mieteinnahmenKaltMonatlich: yup.number().optional().nullable().default(null),
//                         mieteinnahmenWarmMonatlich: yup.number().optional().nullable().default(null),
//                         nebenkostenMonatlich: yup.number().optional().nullable().default(null),
//                         nutzungsart: yup
//                             .string()
//                             .oneOf(['EIGENGENUTZT', 'VERMIETET', 'EIGENGENUTZT_UND_VERMIETET', null])
//                             .optional()
//                             .nullable()
//                             .default(null),
//                         vermieteteWohnflaeche: yup.number().integer().optional().nullable().default(null),
//                         wert: yup.number().optional().nullable().default(null),
//                         wohnflaeche: yup.number().integer().optional().nullable().default(null),
//                     }),
//                 )
//                 .default([]),
//             kinder: yup
//                 .array()
//                 .of(
//                     yup.object().shape({
//                         gehoertZuAntragsteller: yup
//                             .string()
//                             .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                             .required(),
//                         kindergeldFuer: yup
//                             .string()
//                             .oneOf(['ERSTES_ODER_ZWEITES_KIND', 'DRITTES_KIND', 'AB_VIERTEM_KIND', null])
//                             .optional()
//                             .nullable()
//                             .default(null),
//                         unterhaltseinnahmenMonatlich: yup.number().optional().nullable().default(null),
//                     }),
//                 )
//                 .default([]),
//             kontoverbindung: yup.object().shape(
//                 {
//                     bic: yup
//                         .string()
//                         .uppercase()
//                         .optional()
//                         .nullable()
//                         .default(null)
//                         .test(...yupTests.bic),
//                     gehoertZuAntragsteller: yup
//                         .string()
//                         .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE', null])
//                         .optional()
//                         .nullable()
//                         .default(null),
//                     iban: yup
//                         .string()
//                         .optional()
//                         .nullable()
//                         .test(...yupTests.iban),
//                     kreditinstitut: yup.string().optional().nullable().default(null),
//                 },
//                 bankAccountFieldValidationOrder,
//             ),
//             pkw: yup
//                 .array()
//                 .of(
//                     yup.object().shape({
//                         anzahl: yup.number().optional().nullable().default(null),
//                         gehoertZuAntragsteller: yup
//                             .string()
//                             .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                             .required(),
//                         id: yup.string(),
//                     }),
//                 )
//                 .default([]),
//             verbindlichkeiten: yup.object().shape({
//                 dispositionskredite: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 abloesen: yup.boolean().optional().nullable().default(false),
//                                 beanspruchterBetrag: yup.number().optional().nullable().default(null),
//                                 bic: yup
//                                     .string()
//                                     .uppercase()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.bic),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 iban: yup
//                                     .string()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.iban),
//                                 id: yup.string(),
//                                 kreditinstitut: yup.string().optional().nullable().default(null),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 verfuegungsrahmen: yup.number().optional().nullable().default(null),
//                                 zinssatz: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//                 geschaeftskredite: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 abloesen: yup.boolean().optional().nullable().default(false),
//                                 bic: yup
//                                     .string()
//                                     .uppercase()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.bic),
//                                 datumErsteZahlung: yup.date().optional().nullable().default(null),
//                                 datumLetzteRate: yup.date().optional().nullable().default(null),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 iban: yup
//                                     .string()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.iban),
//                                 id: yup.string(),
//                                 kreditinstitut: yup.string().optional().nullable().default(null),
//                                 rateMonatlich: yup.number().optional().nullable().default(null),
//                                 restschuld: yup.number().optional().nullable().default(null),
//                                 schlussrate: yup.number().optional().nullable().default(null),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 urspruenglicherKreditbetrag: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//                 kontokorrentkredite: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 beanspruchterBetrag: yup.number().optional().nullable().default(null),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 id: yup.string(),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 verfuegungsrahmen: yup.number().optional().nullable().default(null),
//                                 zinssatz: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//                 kreditkarten: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 abloesen: yup.boolean().optional().nullable().default(false),
//                                 beanspruchterBetrag: yup.number().optional().nullable().default(null),
//                                 bic: yup
//                                     .string()
//                                     .uppercase()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.bic),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 iban: yup
//                                     .string()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.iban),
//                                 id: yup.string(),
//                                 kreditinstitut: yup.string().optional().nullable().default(null),
//                                 rateMonatlich: yup.number().optional().nullable().default(null),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 verfuegungsrahmen: yup.number().optional().nullable().default(null),
//                                 zinssatz: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//                 leasings: yup
//                     .array()
//                     .of(
//                         yup.object().shape({
//                             datumLetzteRate: yup.date().optional().nullable().default(null),
//                             gehoertZuAntragsteller: yup
//                                 .string()
//                                 .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                 .required(),
//                             glaeubiger: yup.string().optional().nullable().default(null),
//                             id: yup.string(),
//                             rateMonatlich: yup.number().optional().nullable().default(null),
//                             schlussrate: yup.number().optional().nullable().default(null),
//                             source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                         }),
//                     )
//                     .default([]),
//                 ratenkredite: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 abloesen: yup.boolean().optional().nullable().default(false),
//                                 bic: yup
//                                     .string()
//                                     .uppercase()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.bic),
//                                 datumErsteZahlung: yup.date().optional().nullable().default(null),
//                                 datumLetzteRate: yup.date().optional().nullable().default(null),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 iban: yup
//                                     .string()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.iban),
//                                 id: yup.string(),
//                                 kreditinstitut: yup.string().optional().nullable().default(null),
//                                 rateMonatlich: yup.number().optional().nullable().default(null),
//                                 restschuld: yup.number().optional().nullable().default(null),
//                                 schlussrate: yup.number().optional().nullable().default(null),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 urspruenglicherKreditbetrag: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//                 sonstigeVerbindlichkeiten: yup
//                     .array()
//                     .of(
//                         yup.object().shape(
//                             {
//                                 abloesen: yup.boolean().optional().nullable().default(false),
//                                 bic: yup
//                                     .string()
//                                     .uppercase()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.bic),
//                                 datumErsteZahlung: yup.date().optional().nullable().default(null),
//                                 datumLetzteRate: yup.date().optional().nullable().default(null),
//                                 gehoertZuAntragsteller: yup
//                                     .string()
//                                     .oneOf(['ANTRAGSTELLER_1', 'ANTRAGSTELLER_2', 'BEIDE'])
//                                     .required(),
//                                 glaeubiger: yup.string().optional().nullable().default(null),
//                                 iban: yup
//                                     .string()
//                                     .optional()
//                                     .nullable()
//                                     .test(...yupTests.iban),
//                                 id: yup.string(),
//                                 kreditinstitut: yup.string().optional().nullable().default(null),
//                                 rateMonatlich: yup.number().optional().nullable().default(null),
//                                 restschuld: yup.number().optional().nullable().default(null),
//                                 schlussrate: yup.number().optional().nullable().default(null),
//                                 source: yup.string().oneOf(['FORMULAR', 'SCHUFA']).optional().default('FORMULAR'),
//                                 urspruenglicherKreditbetrag: yup.number().optional().nullable().default(null),
//                             },
//                             bankAccountFieldValidationOrder,
//                         ),
//                     )
//                     .default([]),
//             }),
//         }),
//         isFeasible: yup.boolean().optional().nullable().default(false),
//         organization: yup.string().oneOf(ORGANIZATIONS).optional().nullable().default(null),
//         originalApplicants: yup.array().of(yup.string()).min(1).max(2).required(),
//     });
// }
